import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert,
    Grid,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Collapse,
    Menu,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import CustomTooltip from "../../components/CustomTooltip";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import toast from "react-hot-toast";

const buildTree = (mappings, hasTowers) => {
    if (!mappings || mappings.length === 0) return [];
    
    if (hasTowers) {
        return mappings.map(mapping => {
            const towerName = mapping.towerName || "Unnamed Tower";
            const buildNode = (item, currentPath) => {
                const path = currentPath ? `${currentPath} / ${item.title}` : item.title;
                const nodeKey = `${towerName}::${path}`;
                return {
                    id: nodeKey,
                    label: item.title,
                    path: path,
                    towerName: towerName,
                    children: (item.subItems || item.children || []).map(child => buildNode(child, path))
                };
            };
            
            return {
                id: `tower-${towerName}`,
                label: towerName,
                isTower: true,
                towerName: towerName,
                children: (mapping.components || []).map(comp => buildNode(comp, ""))
            };
        });
    } else {
        const mapping = mappings[0];
        const towerName = "default";
        const buildNode = (item, currentPath) => {
            const path = currentPath ? `${currentPath} / ${item.title}` : item.title;
            const nodeKey = `default::${path}`;
            return {
                id: nodeKey,
                label: item.title,
                path: path,
                towerName: towerName,
                children: (item.subItems || item.children || []).map(child => buildNode(child, path))
            };
        };
        return (mapping.components || []).map(comp => buildNode(comp, ""));
    }
};

const buildParentMap = (nodes, parent = null, map = {}) => {
    nodes.forEach(node => {
        map[node.id] = parent;
        if (node.children) {
            buildParentMap(node.children, node, map);
        }
    });
    return map;
};

const getNodeState = (node, checkedIds) => {
    if (!node.children || node.children.length === 0) {
        return checkedIds.has(node.id) ? "checked" : "unchecked";
    }
    
    let checkedCount = 0;
    let uncheckedCount = 0;
    
    for (const child of node.children) {
        const state = getNodeState(child, checkedIds);
        if (state === "checked") {
            checkedCount++;
        } else if (state === "unchecked") {
            uncheckedCount++;
        }
    }
    
    if (checkedCount === node.children.length) {
        return "checked";
    }
    if (uncheckedCount === node.children.length) {
        return "unchecked";
    }
    return "indeterminate";
};

const filterTree = (nodes, query, expandedSet) => {
    if (!query) return nodes;
    
    const lowerQuery = query.toLowerCase();
    
    const filterNode = (node) => {
        const matchesSelf = node.label.toLowerCase().includes(lowerQuery);
        
        let filteredChildren = [];
        if (node.children) {
            filteredChildren = node.children
                .map(child => filterNode(child))
                .filter(Boolean);
        }
        
        const matchesChild = filteredChildren.length > 0;
        
        if (matchesSelf || matchesChild) {
            if (matchesChild) {
                expandedSet.add(node.id);
            }
            return {
                ...node,
                children: filteredChildren
            };
        }
        
        return null;
    };
    
    return nodes.map(node => filterNode(node)).filter(Boolean);
};

const MappingTreeNode = ({ node, checkedIds, onToggle, expandedIds, onExpandToggle, depth = 0 }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const checkedState = getNodeState(node, checkedIds);
    
    const handleCheckboxChange = (e) => {
        e.stopPropagation();
        onToggle(node);
    };
    
    const handleExpandClick = (e) => {
        e.stopPropagation();
        onExpandToggle(node.id);
    };
    
    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box 
                sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    py: 0.5, 
                    pl: depth * 2,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#F7FAFC" },
                    borderRadius: "6px"
                }}
                onClick={hasChildren ? handleExpandClick : undefined}
            >
                <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", mr: 0.5 }}>
                    {hasChildren && (
                        <IconButton size="small" onClick={handleExpandClick} sx={{ p: 0.25 }}>
                            <Icon 
                                name={isExpanded ? "expand-less" : "expand-more"} 
                                size={16} 
                                color={COLORS.SECONDARY} 
                            />
                        </IconButton>
                    )}
                </Box>
                
                <Checkbox
                    size="small"
                    checked={checkedState === "checked"}
                    indeterminate={checkedState === "indeterminate"}
                    onChange={handleCheckboxChange}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        color: COLORS.SECONDARY,
                        "&.Mui-checked": { color: COLORS.SECONDARY },
                        "&.MuiCheckbox-indeterminate": { color: COLORS.SECONDARY },
                        py: 0,
                        px: 0.5,
                        mr: 1
                    }}
                />
                
                <Typography 
                    sx={{ 
                        fontFamily: FONT_FAMILY, 
                        fontSize: node.isTower ? "13px" : "12px", 
                        fontWeight: node.isTower ? 700 : 500,
                        color: node.isTower ? COLORS.PRIMARY : COLORS.TEXT,
                        userSelect: "none"
                    }}
                >
                    {node.label}
                </Typography>
            </Box>
            
            {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        {node.children.map(child => (
                            <MappingTreeNode
                                key={child.id}
                                node={child}
                                checkedIds={checkedIds}
                                onToggle={onToggle}
                                expandedIds={expandedIds}
                                onExpandToggle={onExpandToggle}
                                depth={depth + 1}
                            />
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};

const ProjectDetails = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { token, hasRole } = useAuth() || {};

    const [project, setProject] = useState(null);
    const [reports, setReports] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [engineerMap, setEngineerMap] = useState({});
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);

    // ---- Aggregated Download Modal State ----
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [hasTowers, setHasTowers] = useState(false);
    const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
    
    // New states for Mapping Tree
    const [projectMappings, setProjectMappings] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [checkedIds, setCheckedIds] = useState(new Set());
    const [parentMap, setParentMap] = useState({});
    const [treeSearchQuery, setTreeSearchQuery] = useState("");
    const [expandedIds, setExpandedIds] = useState(new Set());

    const [categoryOptions, setCategoryOptions] = useState([]);   // ["BLOCKWORK", ...]
    const [selectedCategories, setSelectedCategories] = useState(["ALL"]);
    const [reportType, setReportType] = useState("all");

    // Smart Pattern states
    const [aggFilterMode, setAggFilterMode] = useState("tree"); // 'tree' | 'pattern'
    const [aggSelectedTowers, setAggSelectedTowers] = useState(new Set());
    const [aggVerticalSection, setAggVerticalSection] = useState("all"); // 'all' | 'floors_only' | 'parking_only'
    const [aggUnitPatterns, setAggUnitPatterns] = useState([]);
    const [aggUnitPatternInput, setAggUnitPatternInput] = useState("");
    const [aggSelectedTargetRooms, setAggSelectedTargetRooms] = useState(new Set());
    const [aggUniqueRooms, setAggUniqueRooms] = useState([]);

    // ---- Single Report & Group Download Modal State ----
    const [singleDownloadModalOpen, setSingleDownloadModalOpen] = useState(false);
    const [singleReportToDownload, setSingleReportToDownload] = useState(null);
    const [singleDownloadLoading, setSingleDownloadLoading] = useState(false);
    const [singleDownloadProgress, setSingleDownloadProgress] = useState(0);
    
    // ---- Table Grouping State ----
    const [expandedTableGroups, setExpandedTableGroups] = useState({});
    const toggleTableGroup = (gp) => {
        setExpandedTableGroups(prev => ({...prev, [gp]: !prev[gp]}));
    };

    // ---- Export Mapping State ----
    const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    const handleExportMappingClick = (event) => {
        setExportMenuAnchorEl(event.currentTarget);
    };

    const handleExportMappingClose = () => {
        setExportMenuAnchorEl(null);
    };

    const handleExportMapping = async (format) => {
        handleExportMappingClose();
        setExportLoading(true);
        try {
            const res = await axios.get(
                `${url}/api/project-mappings/export-${format}/${projectId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: "blob",
                    timeout: 60000,
                }
            );

            const contentType = res.headers["content-type"] || "";
            if (contentType.includes("application/json")) {
                const text = await res.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.message || "Export failed");
            }

            const blobType = format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            const blob = new Blob([res.data], { type: blobType });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `Project_Mapping_${project?.projectName || "Export"}_${new Date().toISOString().slice(0, 10)}.${format === "pdf" ? "pdf" : "xlsx"}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            toast.success(`Project Mapping exported as ${format.toUpperCase()}!`);
        } catch (err) {
            console.error("Error exporting mapping:", err);
            let errorMessage = `Failed to export ${format.toUpperCase()}. Please try again.`;
            if (err.response && err.response.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const errorData = JSON.parse(text);
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (_) {}
            } else if (err.message) {
                errorMessage = err.message;
            }
            toast.error(errorMessage);
        } finally {
            setExportLoading(false);
        }
    };

    const fetchEngineers = useCallback(async () => {
        try {
            const response = await axios.get(`${url}/api/users/fetch`, {
                params: { role: "ENGINEER", status: "APPROVED" },
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.users) {
                const map = {};
                response.data.users.forEach(user => {
                    map[user._id] = user.name;
                });
                setEngineerMap(map);
            }
        } catch (err) {
            console.error("Error fetching engineers:", err);
        }
    }, [token]);

    const fetchProjectData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch Project Details
            const projectRes = await axios.get(`${url}/api/projects/get/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (projectRes.data.success) {
                setProject(projectRes.data.data);
            }

            // Fetch Field Inspection Reports
            const reportsRes = await axios.get(`${url}/api/field-inspection-reports/fetch`, {
                params: { projectId: projectId },
                headers: { Authorization: `Bearer ${token}` },
            });

            if (reportsRes.data.success) {
                setReports(reportsRes.data.data);
            }

            // Fetch Field Inspection Reports Summary
            const summaryRes = await axios.get(`${url}/api/field-inspection-reports/summary`, {
                params: { projectId: projectId },
                headers: { Authorization: `Bearer ${token}` },
            });

            if (summaryRes.data.success && summaryRes.data.data.projects.length > 0) {
                setSummary(summaryRes.data.data.projects[0].overall);
            }
        } catch (err) {
            console.error("Error fetching project details:", err);
            setError("Failed to load project details");
        } finally {
            setLoading(false);
        }
    }, [projectId, token]);

    useEffect(() => {
        if (token && projectId) {
            fetchEngineers();
            fetchProjectData();
        }
    }, [fetchEngineers, fetchProjectData, token, projectId]);

    const extractUniqueRooms = (mappings, sectionFilter) => {
        const rooms = new Set();
        const traverse = (node, depth) => {
            const isLeaf = !node.subItems || node.subItems.length === 0;
            if (isLeaf) {
                if (depth > 1) {
                    rooms.add(node.title);
                }
                return;
            }
            if (node.subItems && node.subItems.length > 0) {
                node.subItems.forEach(child => traverse(child, depth + 1));
            }
        };
        mappings.forEach(m => {
            if (m.components) {
                m.components.forEach(comp => {
                    let match = true;
                    if (sectionFilter === "floors_only") {
                        match = comp.title.toLowerCase().includes("floor") || comp.title.toLowerCase().includes("level");
                    } else if (sectionFilter === "parking_only") {
                        match = comp.title.toLowerCase().includes("parking") || comp.title.toLowerCase().includes("basement");
                    }
                    if (match) {
                        traverse(comp, 1);
                    }
                });
            }
        });
        return Array.from(rooms).sort();
    };

    useEffect(() => {
        if (projectMappings && projectMappings.length > 0) {
            const rooms = extractUniqueRooms(projectMappings, aggVerticalSection);
            setAggUniqueRooms(rooms);
            setAggSelectedTargetRooms(new Set(rooms));
        }
    }, [aggVerticalSection, projectMappings]);

    // ---- Aggregated Download Handlers ----
    const handleOpenDownloadModal = async () => {
        setDownloadModalOpen(true);
        setSelectedCategories(["ALL"]);
        setReportType("all");
        setTreeSearchQuery("");
        setCheckedIds(new Set());
        setExpandedIds(new Set());
        
        // Reset Smart Pattern states
        setAggFilterMode(project?.source === "FIR" ? "tree" : "tree");
        setAggSelectedTowers(new Set());
        setAggVerticalSection("all");
        setAggUnitPatterns([]);
        setAggUnitPatternInput("");
        setAggSelectedTargetRooms(new Set());
        setAggUniqueRooms([]);

        try {
            // Fetch issue categories
            const catRes = await axios.get(`${url}/api/issue-categories`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (catRes.data.success) {
                setCategoryOptions(catRes.data.data.map((c) => c.name));
            }

            // Fetch Project Mappings
            const mRes = await axios.get(`${url}/api/project-mappings/get/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (mRes.data?.success) {
                const mappings = mRes.data.data;
                setProjectMappings(mappings);
                
                const hasTowersVal = mappings.length > 0 && mappings.some(m => m.towerName);
                setHasTowers(hasTowersVal);
                
                const tree = buildTree(mappings, hasTowersVal);
                setTreeData(tree);
                
                const pMap = buildParentMap(tree);
                setParentMap(pMap);
                
                // Select all by default
                const allIds = new Set();
                const collectAllIds = (nodes) => {
                    nodes.forEach(node => {
                        allIds.add(node.id);
                        if (node.children) collectAllIds(node.children);
                    });
                };
                collectAllIds(tree);
                setCheckedIds(allIds);
                
                // Auto-expand towers
                const initialExpanded = new Set();
                tree.forEach(node => {
                    if (node.isTower) {
                        initialExpanded.add(node.id);
                    }
                });
                setExpandedIds(initialExpanded);

                // Extract rooms & towers for pattern view
                const rooms = extractUniqueRooms(mappings);
                setAggUniqueRooms(rooms);
                setAggSelectedTargetRooms(new Set(rooms));
                
                const towerNamesList = mappings.map(m => m.towerName || 'Unnamed Tower').filter(Boolean);
                setAggSelectedTowers(new Set(towerNamesList));
            }
        } catch (err) {
            console.error("Error loading download options:", err);
            toast.error("Failed to load download options");
        }
    };

    const handleNodeToggle = (node) => {
        setCheckedIds(prev => {
            const next = new Set(prev);
            const currentState = getNodeState(node, prev);
            const shouldCheck = currentState !== "checked";
            
            const setDescendants = (n, val) => {
                if (val) {
                    next.add(n.id);
                } else {
                    next.delete(n.id);
                }
                if (n.children) {
                    n.children.forEach(child => setDescendants(child, val));
                }
            };
            setDescendants(node, shouldCheck);
            
            let parent = parentMap[node.id];
            while (parent) {
                let allChildrenChecked = true;
                for (const child of parent.children) {
                    if (getNodeState(child, next) !== "checked") {
                        allChildrenChecked = false;
                        break;
                    }
                }
                
                if (allChildrenChecked) {
                    next.add(parent.id);
                } else {
                    next.delete(parent.id);
                }
                parent = parentMap[parent.id];
            }
            
            return next;
        });
    };

    const handleExpandToggle = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAll = () => {
        const allIds = new Set();
        const collectAllIds = (nodes) => {
            nodes.forEach(node => {
                allIds.add(node.id);
                if (node.children) collectAllIds(node.children);
            });
        };
        collectAllIds(treeData);
        setCheckedIds(allIds);
    };

    const handleDeselectAll = () => {
        setCheckedIds(new Set());
    };

    const handleCategoryChange = (event) => {
        const value = event.target.value;
        const selected = typeof value === "string" ? value.split(",") : value;

        if (selected.includes("ALL") && !selectedCategories.includes("ALL")) {
            setSelectedCategories(["ALL"]);
        } else if (selected.includes("ALL") && selected.length > 1) {
            setSelectedCategories(selected.filter((c) => c !== "ALL"));
        } else if (selected.length === 0) {
            setSelectedCategories(["ALL"]);
        } else {
            setSelectedCategories(selected);
        }
    };

    useEffect(() => {
        if (treeSearchQuery) {
            const autoExpanded = new Set();
            filterTree(treeData, treeSearchQuery, autoExpanded);
            if (autoExpanded.size > 0) {
                setExpandedIds(prev => {
                    const next = new Set(prev);
                    let changed = false;
                    autoExpanded.forEach(id => {
                        if (!next.has(id)) {
                            next.add(id);
                            changed = true;
                        }
                    });
                    return changed ? next : prev;
                });
            }
        }
    }, [treeSearchQuery, treeData]);

    const filteredTreeData = React.useMemo(() => {
        if (!treeSearchQuery) return treeData;
        const tempExpanded = new Set();
        return filterTree(treeData, treeSearchQuery, tempExpanded);
    }, [treeData, treeSearchQuery]);

    const selectedCount = React.useMemo(() => {
        const normalizeStr = s => String(s || "").toLowerCase().replace(/\s+/g, "");

        const activePatterns = [...aggUnitPatterns];
        const cleanInput = aggUnitPatternInput.trim();
        if (cleanInput) {
            activePatterns.push(cleanInput);
        }

        if (aggFilterMode === "pattern") {
            let count = 0;
            const countPatternMatches = (node, depth, pathNodes, matchesTower) => {
                const currentPathNodes = [...pathNodes, node];
                const isLeaf = !node.subItems || node.subItems.length === 0;
                
                if (isLeaf) {
                    if (!matchesTower) return;
                    
                    const compNode = currentPathNodes[0];
                    let sectionMatch = true;
                    if (aggVerticalSection === "floors_only") {
                        sectionMatch = compNode.title.toLowerCase().includes("floor") || compNode.title.toLowerCase().includes("level");
                    } else if (aggVerticalSection === "parking_only") {
                        sectionMatch = compNode.title.toLowerCase().includes("parking") || compNode.title.toLowerCase().includes("basement");
                    }
                    
                    let unitMatch = true;
                    if (activePatterns.length > 0 && currentPathNodes.length >= 3) {
                        const unitNode = currentPathNodes[2];
                        unitMatch = activePatterns.some(pat => 
                            normalizeStr(unitNode.title).includes(normalizeStr(pat))
                        );
                    }
                    
                    let roomMatch = true;
                    if (aggSelectedTargetRooms.size > 0) {
                        roomMatch = aggSelectedTargetRooms.has(node.title);
                    }
                    
                    if (sectionMatch && unitMatch && roomMatch) {
                        count++;
                    }
                } else {
                    if (node.subItems && node.subItems.length > 0) {
                        node.subItems.forEach((child) => {
                            countPatternMatches(child, depth + 1, currentPathNodes, matchesTower);
                        });
                    }
                }
            };
            
            projectMappings.forEach(m => {
                const tName = m.towerName || 'Unnamed Tower';
                const matchesTower = aggSelectedTowers.size === 0 || aggSelectedTowers.has(tName);
                if (m.components && m.components.length > 0) {
                    m.components.forEach(comp => {
                        countPatternMatches(comp, 1, [], matchesTower);
                    });
                }
            });
            return count;
        } else {
            return Array.from(checkedIds).filter(id => !id.startsWith("tower-")).length;
        }
    }, [aggFilterMode, checkedIds, projectMappings, aggSelectedTowers, aggVerticalSection, aggUnitPatterns, aggUnitPatternInput, aggSelectedTargetRooms]);

    const handleDownloadPDF = async () => {
        let payload = {};
        
        if (aggFilterMode === "pattern") {
            if (selectedCount === 0) {
                toast.error("Please configure the filters to select at least one location.");
                return;
            }
            const activePatterns = [...aggUnitPatterns];
            const cleanInput = aggUnitPatternInput.trim();
            if (cleanInput) {
                activePatterns.push(cleanInput);
            }
            payload = {
                projectId,
                type: reportType,
                filterMode: "pattern",
                towerNames: Array.from(aggSelectedTowers),
                verticalSection: aggVerticalSection,
                unitPatterns: activePatterns,
                targetRooms: Array.from(aggSelectedTargetRooms),
                issueCategories: selectedCategories,
            };
        } else {
            // Collect checked paths and towers
            const pathsToSend = [];
            const towersToSend = new Set();
            
            checkedIds.forEach(id => {
                if (id.startsWith("tower-")) return;
                
                pathsToSend.push(id);
                
                if (id.includes("::")) {
                    const tName = id.split("::")[0];
                    if (tName !== "default") {
                        towersToSend.add(tName);
                    }
                }
            });

            if (pathsToSend.length === 0) {
                toast.error("Please select at least one level/scope to download.");
                return;
            }

            payload = {
                projectId,
                type: reportType,
                filterMode: "tree",
                nodePaths: pathsToSend,
                issueCategories: selectedCategories,
                towerNames: Array.from(towersToSend),
            };
        }

        setDownloadLoading(true);
        setDownloadProgress(0);
        
        const progressInterval = setInterval(() => {
            setDownloadProgress(prev => {
                if (prev >= 95) return prev;
                return prev + Math.floor(Math.random() * 5) + 1;
            });
        }, 500);

        try {
            const res = await axios.post(
                `${url}/api/field-inspection-reports/download-aggregated`,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: "blob",
                    timeout: 900000, // 15 min timeout for large aggregated PDF generation
                }
            );

            // Check if the response is actually an error (JSON disguised as blob)
            const contentType = res.headers["content-type"] || "";
            if (contentType.includes("application/json")) {
                const text = await res.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.message || "PDF generation failed");
            }

            let filename = `FIR_${project?.projectName || "Report"}_${new Date().toISOString().slice(0, 10)}.pdf`;
            const disposition = res.headers["content-disposition"];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            const blob = new Blob([res.data], { type: "application/pdf" });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            setDownloadProgress(100);
            setTimeout(() => {
                toast.success("PDF downloaded successfully!");
                setDownloadModalOpen(false);
            }, 500);
        } catch (err) {
            console.error("Error downloading PDF:", err);

            let errorMessage = "Failed to generate PDF. Please try again.";
            if (err.response && err.response.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const errorData = JSON.parse(text);
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (_) {}
            } else if (err.message) {
                errorMessage = err.message;
            }
            toast.error(errorMessage);
        } finally {
            clearInterval(progressInterval);
            setTimeout(() => {
                setDownloadLoading(false);
                setDownloadProgress(0);
            }, 500);
        }
    };

    const handleOpenSingleDownloadModal = async (report) => {
        setSingleReportToDownload(report);
        setSelectedCategories(["ALL"]);
        setReportType("all");
        setSingleDownloadModalOpen(true);

        try {
            if (categoryOptions.length === 0) {
                const catRes = await axios.get(`${url}/api/issue-categories`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (catRes.data.success) {
                    setCategoryOptions(catRes.data.data.map((c) => c.name));
                }
            }
        } catch (err) {
            console.error("Error loading categories:", err);
            toast.error("Failed to load issue categories");
        }
    };

    const handleSingleDownloadPDF = async () => {
        if (!singleReportToDownload) return;
        setSingleDownloadLoading(true);
        setSingleDownloadProgress(0);
        const progressInterval = setInterval(() => {
            setSingleDownloadProgress(prev => {
                if (prev >= 95) return prev;
                return prev + Math.floor(Math.random() * 5) + 1;
            });
        }, 500);

        try {
            let res;
            if (singleReportToDownload.isGroup) {
                const payload = {
                    projectId,
                    type: reportType,
                    nodePaths: [singleReportToDownload.groupPath],
                    issueCategories: selectedCategories,
                    towerNames: [],
                };
                res = await axios.post(
                    `${url}/api/field-inspection-reports/download-aggregated`,
                    payload,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: "blob",
                        timeout: 900000, // 15 min timeout for large aggregated PDF generation
                    }
                );
            } else {
                const queryParams = new URLSearchParams();
                queryParams.append("type", reportType);
                if (selectedCategories.length > 0) {
                    queryParams.append("issueCategories", selectedCategories.join(","));
                }
                res = await axios.get(
                    `${url}/api/field-inspection-reports/${singleReportToDownload._id}/download?${queryParams.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: "blob",
                        timeout: 900000, // 15 min timeout for large PDF generation
                    }
                );
            }

            const contentType = res.headers["content-type"] || "";
            if (contentType.includes("application/json")) {
                const text = await res.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.message || "PDF generation failed");
            }

            const blob = new Blob([res.data], { type: "application/pdf" });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `FIR_${project?.projectName || "Report"}_Single_${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            setSingleDownloadProgress(100);
            setTimeout(() => {
                toast.success("PDF downloaded successfully!");
                setSingleDownloadModalOpen(false);
            }, 500);
        } catch (err) {
            console.error("Error downloading single PDF:", err);
            let errorMessage = "Failed to generate PDF. Please try again.";
            if (err.response && err.response.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const errorData = JSON.parse(text);
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (_) {}
            } else if (err.message) {
                errorMessage = err.message;
            }
            toast.error(errorMessage);
        } finally {
            clearInterval(progressInterval);
            setTimeout(() => {
                setSingleDownloadLoading(false);
                setSingleDownloadProgress(0);
            }, 500);
        }
    };

    const handleOpenGroupDownloadModal = async (group) => {
        setSingleReportToDownload({ isGroup: true, ...group });
        setSelectedCategories(["ALL"]);
        setReportType("all");
        setSingleDownloadModalOpen(true);

        try {
            if (categoryOptions.length === 0) {
                const catRes = await axios.get(`${url}/api/issue-categories`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (catRes.data.success) {
                    setCategoryOptions(catRes.data.data.map((c) => c.name));
                }
            }
        } catch (err) {
            console.error("Error loading categories:", err);
            toast.error("Failed to load issue categories");
        }
    };



    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleDeleteClick = (reportId) => {
        setReportToDelete(reportId);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!reportToDelete) return;
        try {
            const response = await axios.delete(`${url}/api/field-inspection-reports/${reportToDelete}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                toast.success("Inspection report deleted successfully");
                fetchProjectData();
            }
        } catch (error) {
            console.error("Error deleting report:", error);
            toast.error("Failed to delete inspection report");
        } finally {
            setDeleteConfirmOpen(false);
            setReportToDelete(null);
        }
    };

    const getEngineerName = (id) => {
        if (!id) return "-";
        if (typeof id === 'object' && id.name) return id.name;
        return engineerMap[id] || id;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return "00";
        const date = new Date(dateString);
        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    if (loading) {
        return (
            <Box sx={{ height: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress sx={{ color: COLORS.PRIMARY }} />
            </Box>
        );
    }

    if (error || !project) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error || "Project not found"}</Alert>
            </Box>
        );
    }

    const headerStyle = {
        fontFamily: FONT_FAMILY,
        fontSize: "14px",
        color: COLORS.SECONDARY,
        fontWeight: 600,
        backgroundColor: "#F8F9FA",
        py: 1.5,
        px: 2,
    };

    const sectionPaperStyle = {
        p: { xs: 2.5, md: 3.5 },
        mb: 0,
        borderRadius: "20px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
        backgroundColor: "#fff",
        border: `1px solid ${COLORS.BORDER || "#f0f0f0"}`,
        height: "100%",
        display: "flex",
        flexDirection: "column"
    };

    const sectionHeaderStyle = {
        fontFamily: FONT_FAMILY,
        fontSize: "18px",
        color: COLORS.PRIMARY,
        fontWeight: 700,
        mb: 2,
        display: "flex",
        alignItems: "center",
        gap: 1
    };

    const cellStyle = {
        fontFamily: FONT_FAMILY,
        fontSize: "13px",
        color: COLORS.TEXT,
        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
        py: 2,
        px: 1,
    };

    const labelStyle = {
        fontSize: "14px",
        color: COLORS.SECONDARY,
        fontWeight: 600,
        fontFamily: FONT_FAMILY,
        whiteSpace: "nowrap"
    };

    const valueStyle = {
        fontSize: "14px",
        color: COLORS.TEXT,
        fontWeight: 500,
        fontFamily: FONT_FAMILY,
    };

    const groupedReportsObj = {};
    reports.forEach(r => {
        let gp = "Other";
        if (r.nodePath) {
            const cleanNodePath = r.nodePath.replace(/\s*→\s*/g, ' / ');
            const parts = cleanNodePath.split(' / ').map(p => p.trim()).filter(Boolean);
            if (parts.length > 4) {
                gp = parts.slice(0, 4).join(" / ");
            } else {
                gp = parts.join(" / ");
            }
        }

        if (!groupedReportsObj[gp]) {
            groupedReportsObj[gp] = {
                groupPath: gp,
                latestReportDate: r.reportDate,
                latestCreatedAt: r.createdAt,
                reports: [],
                engineerIds: new Set()
            };
        }
        groupedReportsObj[gp].reports.push(r);
        groupedReportsObj[gp].engineerIds.add(r.engineerId?._id || r.engineerId);
        if (new Date(r.reportDate) > new Date(groupedReportsObj[gp].latestReportDate)) {
            groupedReportsObj[gp].latestReportDate = r.reportDate;
        }
        if (new Date(r.createdAt) > new Date(groupedReportsObj[gp].latestCreatedAt)) {
            groupedReportsObj[gp].latestCreatedAt = r.createdAt;
        }
    });

    const groupedReportsArray = Object.values(groupedReportsObj).sort((a, b) => new Date(b.latestReportDate) - new Date(a.latestReportDate));

    const consultantFields = [
        { label: "Architect", value: project.architect },
        { label: "Structural Consultant", value: project.structuralConsultant },
        { label: "MEP Consultant", value: project.mepConsultant },
        { label: "Interior Designer", value: project.interiorDesigner },
        { label: "Individual", value: project.individual },
        { label: "Other Consultant", value: project.otherConsultant }
    ].filter(field => field.value && field.value !== "-");

    const hasReport = (reports && reports.length > 0) || project.hasReport || (project.reportCount && project.reportCount > 0) || (project.fieldInspectionSummary && project.fieldInspectionSummary.totalReports > 0);
    const isCompleted = hasReport || project.firStatus === "Completed Inspection" || project.firStatus === "Report Submitted" || project.projectStatus === "completed" || project.status === "FINALREPORT";

    return (
        <Box sx={{ width: "100%" }}>
            {/* Header Section */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconButton
                        size="medium"
                        onClick={() => navigate(-1)}
                        sx={{
                            backgroundColor: "#fff",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            color: COLORS.PRIMARY,
                            border: `1px solid ${COLORS.LIGHT_SECONDARY}30`,
                            width: 40,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            "&:hover": { backgroundColor: COLORS.PRIMARY, color: "#fff" }
                        }}
                    >
                        <Icon name="chevron-left" size={20} />
                    </IconButton>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontFamily: FONT_FAMILY,
                                fontSize: "28px",
                                color: COLORS.SECONDARY,
                                fontWeight: 800,
                            }}
                        >
                            {project.projectName}
                        </Typography>
                        <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.LIGHT_SECONDARY, mt: 0.5 }}>
                            Project Details & Inspection History
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    {!hasRole?.("CLIENT") && (
                        <>
                            <CustomTooltip title="Export Mapping">
                                <IconButton
                                    size="medium"
                                    onClick={handleExportMappingClick}
                                    disabled={exportLoading}
                                    sx={{
                                        backgroundColor: "#fff",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                        color: COLORS.SECONDARY,
                                        "&:hover": { backgroundColor: COLORS.SECONDARY, color: "#fff" }
                                    }}
                                >
                                    {exportLoading ? <CircularProgress size={20} color="inherit" /> : <Icon name="download" size={20} />}
                                </IconButton>
                            </CustomTooltip>
                            <Menu
                                anchorEl={exportMenuAnchorEl}
                                open={Boolean(exportMenuAnchorEl)}
                                onClose={handleExportMappingClose}
                                PaperProps={{
                                    elevation: 0,
                                    sx: {
                                        overflow: 'visible',
                                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                                        mt: 1.5,
                                        '& .MuiAvatar-root': {
                                            width: 32,
                                            height: 32,
                                            ml: -0.5,
                                            mr: 1,
                                        },
                                    },
                                }}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            >
                                <MenuItem onClick={() => handleExportMapping("pdf")}>
                                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "14px" }}>Export as PDF</Typography>
                                </MenuItem>
                                <MenuItem onClick={() => handleExportMapping("excel")}>
                                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "14px" }}>Export as Excel</Typography>
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                    {isCompleted && (
                        <CustomTooltip title="Download Aggregated Report">
                            <IconButton
                                size="medium"
                                onClick={handleOpenDownloadModal}
                                sx={{
                                    backgroundColor: "#fff",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    color: COLORS.SECONDARY,
                                    "&:hover": { backgroundColor: COLORS.SECONDARY, color: "#fff" }
                                }}
                            >
                                <Icon name="download1" size={20} />
                            </IconButton>
                        </CustomTooltip>
                    )}
                    {(!hasRole?.("ENGINEER") && (!hasRole?.("CLIENT") || !project?.assignedEngineers?.length)) && (
                        <CustomTooltip title="Edit Project">
                            <IconButton
                                size="medium"
                                onClick={() => {
                                    if (project?.source === "FIR" || project?.firDetails || project?.propertyType) {
                                        navigate(`/admin/fir-requests/edit/${project.firDetails?._id || project._id}`, { state: { request: project.firDetails || project } });
                                    } else {
                                        navigate(`/technical/add-project`, { state: { project } });
                                    }
                                }}
                                sx={{
                                    backgroundColor: "#fff",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                    color: COLORS.SECONDARY,
                                    "&:hover": { backgroundColor: COLORS.SECONDARY, color: "#fff" }
                                }}
                            >
                                <Icon name="edit1" size={20} />
                            </IconButton>
                        </CustomTooltip>
                    )}
                    <CustomTooltip title="View Mapping">
                        <IconButton
                            size="medium"
                            onClick={() => {
                                const targetId = project?.associatedProject?._id || project?.associatedProject || project?._id || projectId;
                                navigate(`/technical/view-mapping/${targetId}`);
                            }}
                            sx={{
                                backgroundColor: "#fff",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                color: COLORS.SECONDARY,
                                "&:hover": { backgroundColor: COLORS.SECONDARY, color: "#fff" }
                            }}
                        >
                            <Icon name="sitemap" size={18} />
                        </IconButton>
                    </CustomTooltip>
                    <CustomTooltip title="Close">
                        <IconButton
                            size="medium"
                            onClick={() => navigate(-1)}
                            sx={{
                                backgroundColor: "#fff",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                color: COLORS.REJECTED || "#FF6961",
                                "&:hover": { backgroundColor: COLORS.REJECTED || "#FF6961", color: "#fff" }
                            }}
                        >
                            <Icon name="close2" size={20} stroke={2.5} />
                        </IconButton>
                    </CustomTooltip>
                </Box>
            </Box>

            <Grid container spacing={4} sx={{ alignItems: "stretch" }}>
                {/* Project Overview & Team Section */}
                <Grid item xs={12} sx={{ width: "100%" }}>
                    <Paper sx={{ ...sectionPaperStyle, width: "100%", p: 4 }} elevation={0}>
                        <Grid container spacing={23} alignItems="stretch" justifyContent="center">
                            {/* Project Overview Column */}
                            <Grid item xs={12} lg={5.5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box sx={{ width: '100%', maxWidth: '650px' }}>
                                    <Typography sx={{ ...sectionHeaderStyle, mb: 1.5, textAlign: "center", justifyContent: "center" }}>
                                        <Icon name="Document" size={20} color={COLORS.PRIMARY} />
                                        Project Overview
                                    </Typography>
                                    <Divider sx={{ mb: 3, opacity: 0.6 }} />
                                    <Grid container spacing={12} justifyContent="center">
                                        {/* Left Sub-Column */}
                                        <Grid item xs={6}>
                                            <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                                <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Developer</Typography>
                                                <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                                    {project.companyName || project.builderName || project.firDetails?.builderName || project.clientUser?.companyName || "-"}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                                <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Address</Typography>
                                                <Typography sx={{ ...valueStyle, fontSize: "13px", fontWeight: 600, color: COLORS.SECONDARY, lineHeight: 1.4, wordBreak: "break-word", maxWidth: "210px" }}>
                                                    {project.address || (project.firDetails?.fullAddress ? `${project.firDetails.fullAddress}${project.firDetails.city ? `, ${project.firDetails.city}` : ""}` : "-")}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                                <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Project Category</Typography>
                                                <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                                    {project.propertyType || project.projectType || project.firDetails?.propertyType || "Property Mapping"}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        {/* Right Sub-Column */}
                                        <Grid item xs={6}>
                                            <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                                <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Client Name</Typography>
                                                <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                                    {project.clientName || project.firDetails?.fullName || project.clientUser?.name || "-"}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                                <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Status</Typography>
                                                {(() => {
                                                    const label = isCompleted ? "Report Submitted" : (project.projectStatus === "inprogress" ? "In Progress" : (project.projectStatus || project.firStatus || "In Progress"));
                                                    const isSuccessColor = isCompleted || project.projectStatus === "completed" || project.firStatus === "Completed Inspection";
                                                    return (
                                                        <Chip
                                                            label={label}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: isSuccessColor ? "#E8F5E9" : "#FFF3E0",
                                                                color: isSuccessColor ? "#2E7D32" : "#EF6C00",
                                                                fontWeight: 600,
                                                                fontSize: "11px",
                                                                textTransform: "capitalize",
                                                                borderRadius: "6px"
                                                            }}
                                                        />
                                                    );
                                                })()}
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                                <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Contact Number</Typography>
                                                <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                                    {project.contactNumber || project.firDetails?.mobileNumber || project.clientUser?.phone || "-"}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Vertical Divider */}
                            <Grid item xs={12} lg={1} sx={{ display: { xs: 'none', lg: 'flex' }, justifyContent: 'center' }}>
                                <Divider orientation="vertical" flexItem />
                            </Grid>

                            {/* Assigned Team Column */}
                            <Grid item xs={12} lg={5.5} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box sx={{ width: '100%', maxWidth: '340px' }}>
                                    <Typography sx={{ ...sectionHeaderStyle, mb: 1.5, textAlign: "center", justifyContent: "center" }}>
                                        <Icon name="user2" size={20} color={COLORS.PRIMARY} />
                                        Assigned Team
                                    </Typography>
                                    <Divider sx={{ mb: 3, opacity: 0.6 }} />
                                    <Box sx={{ mb: 2.5, textAlign: "center" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Project Manager</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.projectManager || "-"}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: "center" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1 }}>Field Engineers</Typography>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexWrap: 'wrap',
                                            justifyContent: 'center',
                                            gap: 1, 
                                        }}>
                                            {project.assignedEngineers?.length > 0 ? (
                                                project.assignedEngineers.map(eng => (
                                                    <Chip
                                                        key={typeof eng === 'string' ? eng : eng._id}
                                                        label={getEngineerName(eng)}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: "#F0F4F8",
                                                            color: COLORS.PRIMARY,
                                                            fontWeight: 600,
                                                            fontSize: "12px",
                                                            borderRadius: "6px",
                                                        }}
                                                    />
                                                ))
                                            ) : (
                                                <Typography sx={{ ...valueStyle, fontStyle: "italic", color: COLORS.LIGHT_SECONDARY }}>No engineers assigned</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Consultants Section - Only visible if data exists */}
                {consultantFields.length > 0 && (
                    <Grid item xs={12} sx={{ width: "100%" }}>
                        <Paper sx={{ ...sectionPaperStyle, width: "100%" }} elevation={0}>
                            <Typography sx={{ ...sectionHeaderStyle, justifyContent: "center" }}>
                                <Icon name="Briefcase" size={20} color={COLORS.PRIMARY} />
                                Stakeholders
                            </Typography>
                            <Divider sx={{ mb: 3, opacity: 0.6 }} />
                                <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ width: "100%" }}>
                                    {consultantFields.map((field, index) => (
                                        <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                                        <Box sx={{ 
                                            p: 2.5, 
                                            borderRadius: "16px", 
                                            backgroundColor: "#F9FAFB", 
                                            border: "1px solid #F0F2F5",
                                            textAlign: "center",
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            minHeight: "80px"
                                        }}>
                                            <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "1px", mb: 0.5 }}>{field.label}</Typography>
                                            <Typography sx={{ ...valueStyle, fontSize: "15px", fontWeight: 600 }}>{field.value}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>
                )}

                {/* Client FIR Request Details Section - Visible if FIR details exist */}
                {(project.firDetails || project.source === "FIR") && (
                    <Grid item xs={12} sx={{ width: "100%" }}>
                        <Paper sx={{ ...sectionPaperStyle, width: "100%", p: 4 }} elevation={0}>
                            <Typography sx={{ ...sectionHeaderStyle, mb: 1.5 }}>
                                <Icon name="clipboardCheck" size={20} color={COLORS.PRIMARY} />
                                Client FIR Request Details
                            </Typography>
                            <Divider sx={{ mb: 3, opacity: 0.6 }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Building / Society Name</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.firDetails?.buildingName || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Wing / Tower</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.firDetails?.wing || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Floor & Unit / Flat No</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                            {project.firDetails?.floor ? `Floor ${project.firDetails.floor}` : ""}{project.firDetails?.unitNumber ? ` (Flat ${project.firDetails.unitNumber})` : "-"}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Alternate Contact</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.firDetails?.altContactNumber || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Preferred Inspection Dates</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "13px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                            {project.firDetails?.inspectionDateFrom ? `${new Date(project.firDetails.inspectionDateFrom).toLocaleDateString()} - ${project.firDetails.inspectionDateTo ? new Date(project.firDetails.inspectionDateTo).toLocaleDateString() : ""}` : "-"}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Preferred Time Slot</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.firDetails?.preferredTimeSlot || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Property Status & Purpose</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "13px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                            {project.firDetails?.propertyStatus || "-"}{project.firDetails?.purposeOfInspection ? ` (${project.firDetails.purposeOfInspection})` : ""}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Site Access & Gate Pass</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "13px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                            Access: {project.firDetails?.accessAvailable ? "Yes" : "No"} | Gate Pass: {project.firDetails?.securityGatePass ? "Yes" : "No"}
                                        </Typography>
                                    </Box>
                                </Grid>
                                {/* New Fields */}
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Developer / Builder</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.firDetails?.builderName || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Project / Scheme</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.firDetails?.projectName || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Tower / Phase</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.firDetails?.towerPhase || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>RERA Number</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>{project.firDetails?.reraNumber || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Site Contact Person</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "14px", fontWeight: 600, color: COLORS.SECONDARY }}>
                                            {project.firDetails?.contactPersonName ? `${project.firDetails.contactPersonName} (${project.firDetails.contactPersonMobile || "No Number"})` : "-"}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Landmark</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "13px", fontWeight: 600, color: COLORS.SECONDARY, whiteSpace: "normal" }}>{project.firDetails?.landmark || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Access Instructions</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "13px", fontWeight: 600, color: COLORS.SECONDARY, whiteSpace: "normal" }}>{project.firDetails?.accessInstructions || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Special Instructions</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "13px", fontWeight: 600, color: COLORS.SECONDARY, whiteSpace: "normal" }}>{project.firDetails?.specialInstructions || "-"}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, borderRadius: "12px", backgroundColor: "#F9FAFB", border: "1px solid #F0F2F5", height: "100%" }}>
                                        <Typography sx={{ ...labelStyle, fontSize: "11px", color: COLORS.LIGHT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>Areas of Concern</Typography>
                                        <Typography sx={{ ...valueStyle, fontSize: "13px", fontWeight: 600, color: COLORS.SECONDARY, whiteSpace: "normal" }}>{project.firDetails?.areasOfConcern || "-"}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                )}

                {/* Summary Section */}
                {summary && (
                    <Grid item xs={12} sx={{ width: "100%" }}>
                        <Paper sx={{ ...sectionPaperStyle, width: "100%" }} elevation={0}>
                            <Typography sx={{ ...sectionHeaderStyle, justifyContent: "center" }}>
                                <Icon name="chart" size={20} color={COLORS.PRIMARY} />
                                Inspection Summary
                            </Typography>
                            <Divider sx={{ mb: 3, opacity: 0.6 }} />
                            <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ width: "100%" }}>
                                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                    <Box sx={{ 
                                        p: 2.5, 
                                        textAlign: "center", 
                                        borderRadius: "16px", 
                                        backgroundColor: "#F7FAFC", 
                                        border: "1px solid #E2E8F0",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        minHeight: "80px"
                                    }}>
                                        <Typography sx={{ fontSize: "12px", color: COLORS.LIGHT_SECONDARY, fontWeight: 600, mb: 0.5 }}>TOTAL REPORTS</Typography>
                                        <Typography sx={{ fontSize: "22px", color: COLORS.PRIMARY, fontWeight: 700 }}>{summary.totalReports}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                    <Box sx={{ 
                                        p: 2.5, 
                                        textAlign: "center", 
                                        borderRadius: "16px", 
                                        backgroundColor: "#F0FFF4", 
                                        border: "1px solid #C6F6D5",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        minHeight: "80px"
                                    }}>
                                        <Typography sx={{ fontSize: "12px", color: "#2F855A", fontWeight: 600, mb: 0.5 }}>YES (TOTAL)</Typography>
                                        <Typography sx={{ fontSize: "22px", color: "#38A169", fontWeight: 700 }}>{summary.yesCount}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                    <Box sx={{ 
                                        p: 2.5, 
                                        textAlign: "center", 
                                        borderRadius: "16px", 
                                        backgroundColor: "#FFF5F5", 
                                        border: "1px solid #FED7D7",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        minHeight: "80px"
                                    }}>
                                        <Typography sx={{ fontSize: "12px", color: "#C53030", fontWeight: 600, mb: 0.5 }}>NO (TOTAL)</Typography>
                                        <Typography sx={{ fontSize: "22px", color: "#E53E3E", fontWeight: 700 }}>{summary.noCount}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                    <Box sx={{ 
                                        p: 2.5, 
                                        textAlign: "center", 
                                        borderRadius: "16px", 
                                        backgroundColor: "#FFFFF0", 
                                        border: "1px solid #FEFCBF",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        minHeight: "80px"
                                    }}>
                                        <Typography sx={{ fontSize: "12px", color: "#B7791F", fontWeight: 600, mb: 0.5 }}>CRITICAL ITEMS</Typography>
                                        <Typography sx={{ fontSize: "22px", color: "#D69E2E", fontWeight: 700 }}>{summary.criticalCount}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                                    <Box sx={{ 
                                        p: 2.5, 
                                        textAlign: "center", 
                                        borderRadius: "16px", 
                                        backgroundColor: "#EBF8FF", 
                                        border: "1px solid #BEE3F8",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        minHeight: "80px"
                                    }}>
                                        <Typography sx={{ fontSize: "12px", color: "#2B6CB0", fontWeight: 600, mb: 0.5 }}>CLOSED QUESTIONS</Typography>
                                        <Typography sx={{ fontSize: "22px", color: "#3182CE", fontWeight: 700 }}>{summary.closedCount}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                            {/* Categorized Summary */}
                            {summary.categoryIssues && summary.categoryIssues.length > 0 && (() => {
                                const allCategories = [...summary.categoryIssues].sort((a, b) => {
                                    const aActive = ((a.yesCount||0)>0 || (a.noCount||0)>0 || (a.criticalCount||0)>0 || (a.closedCount||0)>0 || (a.naCount||0)>0) ? 1 : 0;
                                    const bActive = ((b.yesCount||0)>0 || (b.noCount||0)>0 || (b.criticalCount||0)>0 || (b.closedCount||0)>0 || (b.naCount||0)>0) ? 1 : 0;
                                    return bActive - aActive;
                                });
                                
                                const displayCategories = allCategories.slice(0, 4);
                                const remainingCount = allCategories.length - 4;
                                
                                return (
                                <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                                    <Box sx={{ 
                                        border: "1px solid #E5E7EB", 
                                        borderRadius: "16px",
                                        overflow: "hidden",
                                        width: "100%",
                                        maxWidth: "600px",
                                        margin: "0 auto",
                                        backgroundColor: "#fff",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                                    }}>
                                        <Box sx={{ 
                                            display: "flex", 
                                            justifyContent: "center", 
                                            alignItems: "center",
                                            p: 2.5,
                                            pb: 2,
                                            position: "relative"
                                        }}>
                                            <Typography sx={{ fontSize: "18px", color: COLORS.SECONDARY, fontWeight: 700, textAlign: "center" }}>
                                                Issue Category Summary
                                            </Typography>
                                            <IconButton size="small" onClick={() => setCategoriesModalOpen(true)} sx={{ color: COLORS.SECONDARY, p: 0.5, position: "absolute", right: 16 }}>
                                                <Icon name="info" size={20} />
                                            </IconButton>
                                        </Box>
                                        
                                        <TableContainer sx={{ px: 2 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>Issue Category</TableCell>
                                                        <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>Yes</TableCell>
                                                        <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>No</TableCell>
                                                        <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>Critical</TableCell>
                                                        <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>Closed</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {displayCategories.map((cat, idx) => (
                                                        <TableRow key={idx} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                            <TableCell sx={{ color: COLORS.SECONDARY, fontWeight: 600, fontSize: "13px", py: 2 }}>{cat.categoryName}</TableCell>
                                                            <TableCell align="center" sx={{ color: "#38A169", fontWeight: 600, py: 2 }}>{cat.yesCount || 0}</TableCell>
                                                            <TableCell align="center" sx={{ color: "#E53E3E", fontWeight: 600, py: 2 }}>{cat.noCount || 0}</TableCell>
                                                            <TableCell align="center" sx={{ color: "#D69E2E", fontWeight: 600, py: 2 }}>{cat.criticalCount || 0}</TableCell>
                                                            <TableCell align="center" sx={{ color: COLORS.LIGHT_SECONDARY, fontWeight: 600, py: 2 }}>{cat.closedCount || 0}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        
                                        {remainingCount > 0 && (
                                            <Box 
                                                sx={{ 
                                                    textAlign: "center", 
                                                    py: 2, 
                                                    cursor: "pointer",
                                                    color: COLORS.LIGHT_SECONDARY,
                                                    "&:hover": { backgroundColor: "#F9FAFB" }
                                                }}
                                                onClick={() => setCategoriesModalOpen(true)}
                                            >
                                                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                                    +{remainingCount} more categories
                                                </Typography>
                                            </Box>
                                        )}
                                        {remainingCount <= 0 && <Box sx={{ pb: 1 }} />}
                                    </Box>
                                </Box>
                                );
                            })()}
                        </Paper>
                    </Grid>
                )}

                {/* Reports Section */}
                <Grid item xs={12} sx={{ width: "100%" }}>
                    <Paper sx={{ ...sectionPaperStyle, overflow: "hidden", width: "100%" }} elevation={0}>
                        <Typography sx={sectionHeaderStyle}>
                            <Icon name="Action2" size={20} color={COLORS.PRIMARY} />
                            Inspection Reports
                        </Typography>
                        <Divider sx={{ mb: 3, opacity: 0.6 }} />

                <TableContainer component={Box} sx={{ boxShadow: "none", border: "none", width: "100%" }}>
                    <Table sx={{ minWidth: 650, width: "100%" }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                                <TableCell sx={headerStyle}>Report Date</TableCell>
                                <TableCell sx={headerStyle}>Last Report Edited By Engineer</TableCell>
                                <TableCell sx={headerStyle}>Report for</TableCell>
                                <TableCell sx={{ ...headerStyle, textAlign: "center" }}>Report Time</TableCell>
                                <TableCell sx={{ ...headerStyle, textAlign: "right" }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reports.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: 0.4 }}>
                                            <Icon name="Document" size={40} />
                                            <Typography sx={{ fontFamily: FONT_FAMILY, mt: 1, fontWeight: 500 }}>
                                                No inspection reports found
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                reports.map((report) => (
                                    <TableRow key={report._id} sx={{ "&:hover": { backgroundColor: "#F9FBFD" }, transition: "0.2s" }}>
                                        <TableCell sx={cellStyle}>
                                            <Typography sx={{ fontWeight: 600, color: COLORS.PRIMARY, fontSize: "14px" }}>{formatDate(report.reportDate)}</Typography>
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: COLORS.SECONDARY }} />
                                                <Typography sx={{ fontSize: "13px", color: COLORS.TEXT }}>
                                                    {getEngineerName(report.engineerId)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={cellStyle}>
                                            <Typography sx={{ fontSize: "14px", color: COLORS.TEXT, fontWeight: 500 }}>
                                                {report.nodePath?.replace(/\s*→\s*/g, ' / ') || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={cellStyle} align="center">
                                            <Typography sx={{ fontSize: "13px", color: COLORS.TEXT }}>{formatTime(report.createdAt)}</Typography>
                                        </TableCell>
                                        <TableCell sx={cellStyle} align="right">
                                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                                <CustomTooltip title="View Detailed Report">
                                                    <IconButton size="small" onClick={() => navigate(`/technical/report/${report._id}`)} sx={{ color: COLORS.TEXT, "&:hover": { backgroundColor: "#EDF2F7", color: COLORS.PRIMARY } }}>
                                                        <Icon name="Action1" size={18} />
                                                    </IconButton>
                                                </CustomTooltip>
                                                <CustomTooltip title="Download PDF">
                                                    <IconButton size="small" onClick={() => handleOpenSingleDownloadModal(report)} sx={{ color: COLORS.SECONDARY, "&:hover": { backgroundColor: "#EBF8FF", color: COLORS.PRIMARY } }}>
                                                        <Icon name="download1" size={18} />
                                                    </IconButton>
                                                </CustomTooltip>
                                                <CustomTooltip title="Delete Report">
                                                    <IconButton size="small" onClick={() => handleDeleteClick(report._id)} sx={{ color: COLORS.REJECTED || "#FF6961", "&:hover": { backgroundColor: "#FFEBEE", color: "#D32F2F" } }}>
                                                        <Icon name="delete" size={18} />
                                                    </IconButton>
                                                </CustomTooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <ConfirmDialog
                open={deleteConfirmOpen}
                title="Delete Inspection Report"
                content="Are you sure you want to delete this inspection report? This action cannot be undone."
                onConfirm={handleConfirmDelete}
                onCancel={() => {
                    setDeleteConfirmOpen(false);
                    setReportToDelete(null);
                }}
            />

            {/* ========== Download Aggregated Report Modal ========== */}
            <Dialog
                open={downloadModalOpen}
                onClose={() => setDownloadModalOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        fontFamily: FONT_FAMILY,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontFamily: FONT_FAMILY,
                        fontWeight: 700,
                        fontSize: "20px",
                        color: COLORS.PRIMARY,
                        pt: 3.5,
                        px: 4,
                        pb: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Icon name="download1" size={24} color={COLORS.PRIMARY} />
                    Download Aggregated Report
                </DialogTitle>
                <Typography sx={{ px: 4, pb: 2.5, fontFamily: FONT_FAMILY, fontSize: "14px", color: COLORS.LIGHT_SECONDARY }}>
                    Choose specific towers, levels, or zones to aggregate and generate a compiled PDF report.
                </Typography>
                <Divider sx={{ opacity: 0.7 }} />
                
                <DialogContent sx={{ pt: "30px !important", px: 4, pb: 3 }}>
                    <Box sx={{ display: "flex", width: "100%", minWidth: { md: "800px" } }}>
                        {/* Left Grid: Collapsible Checkbox Tree */}
                        <Box sx={{ flex: "0 0 70%", maxWidth: "70%", display: "flex", flexDirection: "column", borderRight: `1px solid ${COLORS.BORDER || "#f0f0f0"}`, pr: 4 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "15px", fontWeight: 700, color: COLORS.SECONDARY, mb: 2 }}>
                                Select Levels & Scopes
                            </Typography>
                            
                            {/* Tab toggle */}
                            {project?.source !== "FIR" && (
                                <Box sx={{ display: "flex", backgroundColor: "#f0f2f5", borderRadius: "10px", p: 0.5, mb: 2 }}>
                                    <Button
                                        fullWidth
                                        variant={aggFilterMode === "pattern" ? "contained" : "text"}
                                        onClick={() => setAggFilterMode("pattern")}
                                        sx={{
                                            borderRadius: "8px",
                                            color: aggFilterMode === "pattern" ? "#fff" : COLORS.TEXT,
                                            backgroundColor: aggFilterMode === "pattern" ? COLORS.PRIMARY : "transparent",
                                            boxShadow: aggFilterMode === "pattern" ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
                                            "&:hover": { backgroundColor: aggFilterMode === "pattern" ? COLORS.PRIMARY : "rgba(0,0,0,0.04)" }
                                        }}
                                    >
                                        Smart Pattern Filter
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant={aggFilterMode === "tree" ? "contained" : "text"}
                                        onClick={() => setAggFilterMode("tree")}
                                        sx={{
                                            borderRadius: "8px",
                                            color: aggFilterMode === "tree" ? "#fff" : COLORS.TEXT,
                                            backgroundColor: aggFilterMode === "tree" ? COLORS.PRIMARY : "transparent",
                                            boxShadow: aggFilterMode === "tree" ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
                                            "&:hover": { backgroundColor: aggFilterMode === "tree" ? COLORS.PRIMARY : "rgba(0,0,0,0.04)" }
                                        }}
                                    >
                                        Explicit Tree View
                                    </Button>
                                </Box>
                            )}

                            {aggFilterMode === "tree" ? (
                                <>
                                    {/* Tree Search Box */}
                                    <OutlinedInput
                                        placeholder="Search levels (e.g. Floor 1)..."
                                        size="small"
                                        value={treeSearchQuery}
                                        onChange={(e) => setTreeSearchQuery(e.target.value)}
                                        sx={{ mb: 2, fontFamily: FONT_FAMILY, borderRadius: "10px", fontSize: "13px" }}
                                    />
                                    
                                    {/* Select All / Clear buttons */}
                                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                                        <Button 
                                            size="small" 
                                            onClick={handleSelectAll}
                                            sx={{ fontFamily: FONT_FAMILY, fontSize: "11px", fontWeight: 600, textTransform: "none", color: COLORS.PRIMARY, p: 0 }}
                                        >
                                            Select All
                                        </Button>
                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
                                        <Button 
                                            size="small" 
                                            onClick={handleDeselectAll}
                                            sx={{ fontFamily: FONT_FAMILY, fontSize: "11px", fontWeight: 600, textTransform: "none", color: COLORS.LIGHT_SECONDARY, p: 0 }}
                                        >
                                            Clear Selection
                                        </Button>
                                    </Box>
                                    
                                    {/* Scrollable Tree Wrapper */}
                                    <Box 
                                        sx={{ 
                                            maxHeight: "400px", 
                                            overflowY: "auto", 
                                            border: `1px solid ${COLORS.BORDER || "#f0f0f0"}`, 
                                            borderRadius: "10px", 
                                            p: 1.5, 
                                            backgroundColor: "#FAFAFA" 
                                        }}
                                    >
                                        {filteredTreeData.length === 0 ? (
                                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "12px", color: COLORS.LIGHT_SECONDARY, textAlign: "center", py: 4 }}>
                                                No matching items found
                                            </Typography>
                                        ) : (
                                            filteredTreeData.map(node => (
                                                <MappingTreeNode
                                                    key={node.id}
                                                    node={node}
                                                    checkedIds={checkedIds}
                                                    onToggle={handleNodeToggle}
                                                    expandedIds={expandedIds}
                                                    onExpandToggle={handleExpandToggle}
                                                />
                                            ))
                                        )}
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: "400px", overflowY: "auto", pr: 0.5 }}>
                                    {/* 1. Select Towers */}
                                    {hasTowers && (
                                        <Box>
                                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "12px", fontWeight: 700, color: COLORS.PRIMARY, mb: 1 }}>
                                                1. Select Scopes / Towers
                                            </Typography>
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                {projectMappings.map(m => {
                                                    const tName = m.towerName || 'Unnamed Tower';
                                                    const isSel = aggSelectedTowers.has(tName);
                                                    return (
                                                        <Chip
                                                            key={tName}
                                                            label={tName}
                                                            variant={isSel ? "filled" : "outlined"}
                                                            color={isSel ? "primary" : "default"}
                                                            onClick={() => {
                                                                setAggSelectedTowers(prev => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(tName)) next.delete(tName);
                                                                    else next.add(tName);
                                                                    return next;
                                                                });
                                                            }}
                                                            sx={{ borderRadius: "8px", fontFamily: FONT_FAMILY, fontSize: "11px", height: "26px" }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* 2. Vertical Section */}
                                    <Box>
                                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "12px", fontWeight: 700, color: COLORS.PRIMARY, mb: 1 }}>
                                            2. Vertical Section
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            {[
                                                { id: 'all', label: 'All Sections' },
                                                { id: 'floors_only', label: 'Floors Only' },
                                                { id: 'parking_only', label: 'Parking Only' },
                                            ].map(sec => {
                                                const isSel = aggVerticalSection === sec.id;
                                                return (
                                                    <Button
                                                        key={sec.id}
                                                        size="small"
                                                        variant={isSel ? "contained" : "outlined"}
                                                        onClick={() => setAggVerticalSection(sec.id)}
                                                        sx={{
                                                            flex: 1,
                                                            fontFamily: FONT_FAMILY,
                                                            fontSize: "11px",
                                                            textTransform: "none",
                                                            borderRadius: "8px",
                                                            py: 0.5,
                                                            borderColor: COLORS.BORDER,
                                                            backgroundColor: isSel ? COLORS.SECONDARY : "transparent",
                                                            color: isSel ? "#fff" : COLORS.TEXT,
                                                            "&:hover": { backgroundColor: isSel ? COLORS.PRIMARY : "rgba(0,0,0,0.02)" }
                                                        }}
                                                    >
                                                        {sec.label}
                                                    </Button>
                                                );
                                            })}
                                        </Box>
                                    </Box>

                                    {/* 3. Target Flat / Unit Patterns */}
                                    <Box>
                                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "12px", fontWeight: 700, color: COLORS.PRIMARY, mb: 1 }}>
                                            3. Target Flat / Unit Patterns
                                        </Typography>
                                        <OutlinedInput
                                            placeholder="Type unit pattern & press enter"
                                            size="small"
                                            value={aggUnitPatternInput}
                                            onChange={(e) => setAggUnitPatternInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    const clean = aggUnitPatternInput.trim();
                                                    if (clean && !aggUnitPatterns.includes(clean)) {
                                                        setAggUnitPatterns([...aggUnitPatterns, clean]);
                                                    }
                                                    setAggUnitPatternInput("");
                                                }
                                            }}
                                            sx={{ mb: 1.5, fontFamily: FONT_FAMILY, borderRadius: "10px", fontSize: "13px", width: "100%" }}
                                            endAdornment={
                                                aggUnitPatternInput.trim() && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            const clean = aggUnitPatternInput.trim();
                                                            if (clean && !aggUnitPatterns.includes(clean)) {
                                                                setAggUnitPatterns([...aggUnitPatterns, clean]);
                                                            }
                                                            setAggUnitPatternInput("");
                                                        }}
                                                        sx={{ textTransform: "none", fontSize: "11px", fontWeight: 700 }}
                                                    >
                                                        Add
                                                    </Button>
                                                )
                                            }
                                        />
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {aggUnitPatterns.map(pat => (
                                                <Chip
                                                    key={pat}
                                                    label={pat}
                                                    size="small"
                                                    onDelete={() => setAggUnitPatterns(aggUnitPatterns.filter(p => p !== pat))}
                                                    sx={{ borderRadius: "8px", fontFamily: FONT_FAMILY, fontSize: "11px" }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* 4. Target Spaces / Rooms */}
                                    <Box>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "12px", fontWeight: 700, color: COLORS.PRIMARY }}>
                                                4. Target Spaces / Rooms
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    if (aggSelectedTargetRooms.size === aggUniqueRooms.length) {
                                                        setAggSelectedTargetRooms(new Set());
                                                    } else {
                                                        setAggSelectedTargetRooms(new Set(aggUniqueRooms));
                                                    }
                                                }}
                                                sx={{ fontFamily: FONT_FAMILY, fontSize: "11px", p: 0, textTransform: "none" }}
                                            >
                                                {aggSelectedTargetRooms.size === aggUniqueRooms.length ? "Deselect All" : "Select All"}
                                            </Button>
                                        </Box>
                                        <Box sx={{
                                            border: `1px solid ${COLORS.BORDER || "#f0f0f0"}`,
                                            borderRadius: "10px",
                                            p: 1.5,
                                            maxHeight: "150px",
                                            overflowY: "auto",
                                            backgroundColor: "#FAFAFA"
                                        }}>
                                            {aggUniqueRooms.length === 0 ? (
                                                <Typography sx={{ fontSize: "12px", color: COLORS.LIGHT_SECONDARY, fontFamily: FONT_FAMILY }}>
                                                    No spaces found.
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {aggUniqueRooms.map(room => {
                                                        const isSel = aggSelectedTargetRooms.has(room);
                                                        return (
                                                            <Chip
                                                                key={room}
                                                                label={room}
                                                                variant={isSel ? "filled" : "outlined"}
                                                                color={isSel ? "primary" : "default"}
                                                                onClick={() => {
                                                                    setAggSelectedTargetRooms(prev => {
                                                                        const next = new Set(prev);
                                                                        if (next.has(room)) next.delete(room);
                                                                        else next.add(room);
                                                                        return next;
                                                                    });
                                                                }}
                                                                sx={{ borderRadius: "8px", fontFamily: FONT_FAMILY, fontSize: "11px", height: "26px" }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                        
                        {/* Right Grid: Options and Summary */}
                        <Box sx={{ flex: "0 0 30%", maxWidth: "30%", display: "flex", flexDirection: "column", gap: 3, pl: 4 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "15px", fontWeight: 700, color: COLORS.SECONDARY }}>
                                Report Configuration
                            </Typography>
                            
                            {/* Issue Category Selection */}
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontFamily: FONT_FAMILY }}>Issue Categories</InputLabel>
                                <Select
                                    multiple
                                    value={selectedCategories}
                                    onChange={handleCategoryChange}
                                    input={<OutlinedInput label="Issue Categories" />}
                                    renderValue={(selected) => {
                                        if (selected.includes("ALL")) return "All Categories";
                                        if (selected.length === 0) return "Select Categories";
                                        return selected.length > 2 
                                            ? `${selected.length} Categories Selected` 
                                            : selected.join(", ");
                                    }}
                                    sx={{ fontFamily: FONT_FAMILY, borderRadius: "10px" }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 260,
                                                borderRadius: "12px",
                                                boxShadow: "0 10px 35px rgba(0,0,0,0.18)",
                                                fontFamily: FONT_FAMILY,
                                                "& .MuiMenuItem-root": {
                                                    py: 0.7,
                                                    px: 1.5,
                                                    fontSize: "13px",
                                                    fontFamily: FONT_FAMILY,
                                                    borderRadius: "6px",
                                                    mx: 0.5,
                                                    my: 0.2,
                                                },
                                                "&::-webkit-scrollbar": {
                                                    width: "6px",
                                                },
                                                "&::-webkit-scrollbar-thumb": {
                                                    backgroundColor: COLORS.SECONDARY || "#00b3aa",
                                                    borderRadius: "3px",
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="ALL">
                                        <Checkbox checked={selectedCategories.includes("ALL")} size="small" sx={{ color: COLORS.SECONDARY, "&.Mui-checked": { color: COLORS.SECONDARY } }} />
                                        <ListItemText primary="ALL CATEGORIES" primaryTypographyProps={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: "13px" }} />
                                    </MenuItem>
                                    {categoryOptions.map((cat) => (
                                        <MenuItem key={cat} value={cat}>
                                            <Checkbox checked={selectedCategories.indexOf(cat) > -1} size="small" sx={{ color: COLORS.SECONDARY, "&.Mui-checked": { color: COLORS.SECONDARY } }} />
                                            <ListItemText primary={cat} primaryTypographyProps={{ fontFamily: FONT_FAMILY, fontSize: "13px" }} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            {/* Report Type Selection */}
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontFamily: FONT_FAMILY }}>Report Type</InputLabel>
                                <Select
                                    value={reportType}
                                    onChange={(e) => setReportType(e.target.value)}
                                    input={<OutlinedInput label="Report Type" />}
                                    sx={{ fontFamily: FONT_FAMILY, borderRadius: "10px" }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 220,
                                                borderRadius: "12px",
                                                boxShadow: "0 10px 35px rgba(0,0,0,0.18)",
                                                fontFamily: FONT_FAMILY,
                                                "& .MuiMenuItem-root": {
                                                    py: 1,
                                                    px: 2,
                                                    fontSize: "13px",
                                                    fontFamily: FONT_FAMILY,
                                                    borderRadius: "6px",
                                                    mx: 0.5,
                                                    my: 0.2,
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="all">All Questions</MenuItem>
                                    <MenuItem value="no">Questions with "No" Response</MenuItem>
                                    <MenuItem value="resolved">Resolved Issues (No → Yes)</MenuItem>
                                </Select>
                            </FormControl>
                            
                            {/* Selection Summary Box */}
                            <Box 
                                sx={{ 
                                    mt: "auto", 
                                    p: 2.5, 
                                    borderRadius: "12px", 
                                    backgroundColor: "#F7FAFC", 
                                    border: `1px solid ${COLORS.BORDER || "#E2E8F0"}` 
                                }}
                            >
                                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "13px", fontWeight: 700, color: COLORS.PRIMARY, mb: 1.5 }}>
                                    Selection Overview
                                </Typography>
                                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "12px", color: COLORS.TEXT, mb: 0.5 }}>
                                    • Active Locations selected: <strong>{selectedCount}</strong> items
                                </Typography>
                                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "12px", color: COLORS.TEXT }}>
                                    • Towers: <strong>{
                                        aggFilterMode === "pattern" ? (
                                            aggSelectedTowers.size > 0 ? Array.from(aggSelectedTowers).join(", ") : "None"
                                        ) : (
                                            (() => {
                                                const towersSelectedSet = new Set();
                                                checkedIds.forEach(id => {
                                                    if (id.startsWith("tower-")) return;
                                                    if (id.includes("::")) {
                                                        const tName = id.split("::")[0];
                                                        if (tName !== "default") towersSelectedSet.add(tName);
                                                    }
                                                });
                                                return towersSelectedSet.size > 0 ? Array.from(towersSelectedSet).join(", ") : "All / Default";
                                            })()
                                        )
                                    }</strong>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 4, pb: 3, pt: 1, gap: 1 }}>
                    <Button
                        onClick={() => setDownloadModalOpen(false)}
                        sx={{
                            fontFamily: FONT_FAMILY,
                            fontWeight: 600,
                            color: COLORS.TEXT,
                            borderRadius: "10px",
                            textTransform: "none",
                            px: 3,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDownloadPDF}
                        disabled={downloadLoading}
                        startIcon={
                            downloadLoading ? (
                                <CircularProgress 
                                    variant="determinate" 
                                    value={downloadProgress} 
                                    size={18} 
                                    color="inherit" 
                                />
                            ) : (
                                <Icon name="download1" size={18} />
                            )
                        }
                        sx={{
                            fontFamily: FONT_FAMILY,
                            fontWeight: 600,
                            backgroundColor: COLORS.SECONDARY,
                            borderRadius: "10px",
                            textTransform: "none",
                            px: 3,
                            "&:hover": { backgroundColor: COLORS.PRIMARY },
                        }}
                    >
                        {downloadLoading ? `Generating... ${downloadProgress}%` : "Download PDF"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ========== Download Single Report Modal ========== */}
            <Dialog
                open={singleDownloadModalOpen}
                onClose={() => setSingleDownloadModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        fontFamily: FONT_FAMILY,
                    },
                }}
            >
                <DialogTitle sx={{ 
                    fontFamily: FONT_FAMILY, 
                    fontWeight: 700,
                    color: COLORS.PRIMARY,
                    borderBottom: "1px solid #E2E8F0",
                    pb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                }}>
                    <Icon name="download1" size={24} color={COLORS.SECONDARY} />
                    Download Inspection Report
                </DialogTitle>
                <DialogContent sx={{ pt: "20px !important", px: 3, pb: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
                    
                    {/* Issue Category Selection */}
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontFamily: FONT_FAMILY }}>Issue Categories</InputLabel>
                        <Select
                            multiple
                            value={selectedCategories}
                            onChange={handleCategoryChange}
                            input={<OutlinedInput label="Issue Categories" />}
                            renderValue={(selected) => {
                                if (selected.includes("ALL")) return "All Categories";
                                if (selected.length === 0) return "Select Categories";
                                return selected.length > 2 
                                    ? `${selected.length} Categories Selected` 
                                    : selected.join(", ");
                            }}
                            sx={{ fontFamily: FONT_FAMILY, borderRadius: "10px" }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: 260,
                                        borderRadius: "12px",
                                        boxShadow: "0 10px 35px rgba(0,0,0,0.18)",
                                        fontFamily: FONT_FAMILY,
                                        "& .MuiMenuItem-root": {
                                            py: 0.7,
                                            px: 1.5,
                                            fontSize: "13px",
                                            fontFamily: FONT_FAMILY,
                                            borderRadius: "6px",
                                            mx: 0.5,
                                            my: 0.2,
                                            "&:hover": {
                                                backgroundColor: "rgba(66, 26, 110, 0.06)",
                                            },
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(66, 26, 110, 0.12)",
                                            },
                                        },
                                        "&::-webkit-scrollbar": {
                                            width: "6px",
                                        },
                                        "&::-webkit-scrollbar-track": {
                                            backgroundColor: "#f1f1f1",
                                            borderRadius: "3px",
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: COLORS.SECONDARY || "#00b3aa",
                                            borderRadius: "3px",
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="ALL" sx={{ fontWeight: selectedCategories.includes("ALL") ? 700 : 400 }}>
                                <Checkbox 
                                    checked={selectedCategories.includes("ALL")} 
                                    size="small" 
                                    sx={{ color: COLORS.SECONDARY, "&.Mui-checked": { color: COLORS.SECONDARY } }}
                                />
                                <ListItemText 
                                    primary="ALL CATEGORIES" 
                                    primaryTypographyProps={{ fontFamily: FONT_FAMILY, fontWeight: 700, fontSize: "13px" }}
                                />
                            </MenuItem>
                            {categoryOptions.map((cat) => (
                                <MenuItem key={cat} value={cat}>
                                    <Checkbox 
                                        checked={selectedCategories.includes(cat)} 
                                        size="small" 
                                        sx={{ color: COLORS.SECONDARY, "&.Mui-checked": { color: COLORS.SECONDARY } }}
                                    />
                                    <ListItemText 
                                        primary={cat} 
                                        primaryTypographyProps={{ fontFamily: FONT_FAMILY, fontSize: "13px" }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Report Type Selection */}
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontFamily: FONT_FAMILY }}>Report Type</InputLabel>
                        <Select
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                            input={<OutlinedInput label="Report Type" />}
                            sx={{ fontFamily: FONT_FAMILY, borderRadius: "10px" }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: 220,
                                        borderRadius: "12px",
                                        boxShadow: "0 10px 35px rgba(0,0,0,0.18)",
                                        fontFamily: FONT_FAMILY,
                                        "& .MuiMenuItem-root": {
                                            py: 1,
                                            px: 2,
                                            fontSize: "13px",
                                            fontFamily: FONT_FAMILY,
                                            borderRadius: "6px",
                                            mx: 0.5,
                                            my: 0.2,
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="all">All Questions</MenuItem>
                            <MenuItem value="no">Questions with "No" Response</MenuItem>
                            <MenuItem value="resolved">Resolved Issues (No → Yes)</MenuItem>
                        </Select>
                    </FormControl>

                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setSingleDownloadModalOpen(false)}
                        sx={{
                            fontFamily: FONT_FAMILY,
                            fontWeight: 600,
                            color: COLORS.TEXT,
                            borderRadius: "10px",
                            textTransform: "none",
                            px: 3,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSingleDownloadPDF}
                        disabled={singleDownloadLoading}
                        startIcon={
                            singleDownloadLoading ? (
                                <CircularProgress 
                                    variant="determinate" 
                                    value={singleDownloadProgress} 
                                    size={18} 
                                    color="inherit" 
                                />
                            ) : (
                                <Icon name="download1" size={18} />
                            )
                        }
                        sx={{
                            fontFamily: FONT_FAMILY,
                            fontWeight: 600,
                            backgroundColor: COLORS.SECONDARY,
                            borderRadius: "10px",
                            textTransform: "none",
                            px: 3,
                            "&:hover": { backgroundColor: COLORS.PRIMARY },
                        }}
                    >
                        {singleDownloadLoading ? `Generating... ${singleDownloadProgress}%` : "Download PDF"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Categories Modal */}
            <Dialog 
                open={categoriesModalOpen} 
                onClose={() => setCategoriesModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: "16px", p: 1 }
                }}
            >
                <DialogTitle sx={{ 
                    fontFamily: FONT_FAMILY, 
                    fontWeight: 700, 
                    color: COLORS.SECONDARY,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 1
                }}>
                    Issue Category Summary
                    <IconButton size="small" onClick={() => setCategoriesModalOpen(false)}>
                        <Icon name="close2" size={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, borderTop: "none" }}>
                    <Box sx={{ px: 2, pb: 1 }}>
                        <Typography sx={{ fontSize: "13px", color: COLORS.LIGHT_SECONDARY, mb: 1 }}>
                            Showing {summary?.categoryIssues?.length || 0} categories
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>Issue Category</TableCell>
                                    <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>Yes</TableCell>
                                    <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>No</TableCell>
                                    <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>Critical</TableCell>
                                    <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>Closed</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(() => {
                                    const allCategories = [...(summary?.categoryIssues || [])].sort((a, b) => {
                                        const aActive = ((a.yesCount||0)>0 || (a.noCount||0)>0 || (a.criticalCount||0)>0 || (a.closedCount||0)>0 || (a.naCount||0)>0) ? 1 : 0;
                                        const bActive = ((b.yesCount||0)>0 || (b.noCount||0)>0 || (b.criticalCount||0)>0 || (b.closedCount||0)>0 || (b.naCount||0)>0) ? 1 : 0;
                                        return bActive - aActive;
                                    });
                                    return allCategories.map((cat, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell sx={{ color: COLORS.SECONDARY, fontWeight: 600, fontSize: "13px" }}>{cat.categoryName}</TableCell>
                                            <TableCell align="center" sx={{ color: "#38A169", fontWeight: 600 }}>{cat.yesCount || 0}</TableCell>
                                            <TableCell align="center" sx={{ color: "#E53E3E", fontWeight: 600 }}>{cat.noCount || 0}</TableCell>
                                            <TableCell align="center" sx={{ color: "#D69E2E", fontWeight: 600 }}>{cat.criticalCount || 0}</TableCell>
                                            <TableCell align="center" sx={{ color: COLORS.LIGHT_SECONDARY, fontWeight: 600 }}>{cat.closedCount || 0}</TableCell>
                                        </TableRow>
                                    ));
                                })()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default ProjectDetails;
